import { Component } from '@theme/component';
import { isMobileBreakpoint, mediaQueryLarge, prefersReducedMotion } from '@theme/utilities';

const AUTOPLAY_RESUME_DELAY = 6000;

/**
 * A mobile-only image carousel for the hero section. Desktop rendering is untouched;
 * this component only exists inside the `.hero__media-wrapper--mobile` slot.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} track - The scrollable slide track.
 * @property {HTMLElement[]} slides - The individual slides.
 * @property {HTMLElement[]} [dots] - The pagination dots, if more than one slide.
 *
 * @extends Component<Refs>
 */
class HeroCarouselComponent extends Component {
  requiredRefs = ['track', 'slides'];

  /** @type {IntersectionObserver | null} */
  #observer = null;
  /** @type {number | null} */
  #autoplayId = null;
  /** @type {number | null} */
  #resumeTimeoutId = null;
  #activeIndex = 0;

  connectedCallback() {
    super.connectedCallback();

    this.#observer = new IntersectionObserver(this.#handleIntersect, {
      root: this.refs.track,
      threshold: 0.6,
    });
    for (const slide of this.refs.slides) {
      this.#observer.observe(slide);
    }

    this.refs.track.addEventListener('pointerdown', this.#handleInteraction, { passive: true });
    mediaQueryLarge.addEventListener('change', this.#handleBreakpointChange);

    this.#startAutoplay();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#observer?.disconnect();
    this.refs.track.removeEventListener('pointerdown', this.#handleInteraction);
    mediaQueryLarge.removeEventListener('change', this.#handleBreakpointChange);
    this.#stopAutoplay();
    if (this.#resumeTimeoutId) window.clearTimeout(this.#resumeTimeoutId);
  }

  /**
   * Declarative event target for pagination dots (`on:click="/select/{index}"`).
   * @param {Event} _event
   * @param {number} index
   */
  select(_event, index) {
    this.#scrollToIndex(index);
    this.#pauseThenResume();
  }

  /** @param {IntersectionObserverEntry[]} entries */
  #handleIntersect = (entries) => {
    for (const entry of entries) {
      if (!entry.isIntersecting) continue;
      const index = this.refs.slides.indexOf(/** @type {HTMLElement} */ (entry.target));
      if (index !== -1) this.#setActiveIndex(index);
    }
  };

  /** @param {number} index */
  #setActiveIndex(index) {
    this.#activeIndex = index;
    const dots = this.refs.dots ?? [];
    dots.forEach((dot, i) => {
      dot.classList.toggle('is-active', i === index);
      dot.setAttribute('aria-selected', i === index ? 'true' : 'false');
    });
  }

  /** @param {number} index */
  #scrollToIndex(index) {
    const slide = this.refs.slides[index];
    if (!slide) return;
    this.refs.track.scrollTo({
      left: slide.offsetLeft,
      behavior: prefersReducedMotion() ? 'auto' : 'smooth',
    });
  }

  #handleInteraction = () => {
    this.#pauseThenResume();
  };

  #pauseThenResume() {
    this.#stopAutoplay();
    if (this.#resumeTimeoutId) window.clearTimeout(this.#resumeTimeoutId);
    this.#resumeTimeoutId = window.setTimeout(() => {
      this.#startAutoplay();
    }, AUTOPLAY_RESUME_DELAY);
  }

  #startAutoplay() {
    if (this.#autoplayId) return;
    if (!isMobileBreakpoint()) return;
    if (prefersReducedMotion()) return;

    const { slides } = this.refs;
    if (slides.length < 2) return;

    const speed = Number(this.getAttribute('autoplay')) || 5000;

    this.#autoplayId = window.setInterval(() => {
      const nextIndex = (this.#activeIndex + 1) % slides.length;
      this.#scrollToIndex(nextIndex);
    }, speed);
  }

  #stopAutoplay() {
    if (this.#autoplayId) {
      window.clearInterval(this.#autoplayId);
      this.#autoplayId = null;
    }
  }

  #handleBreakpointChange = () => {
    if (isMobileBreakpoint()) {
      this.#startAutoplay();
    } else {
      this.#stopAutoplay();
      if (this.#resumeTimeoutId) {
        window.clearTimeout(this.#resumeTimeoutId);
        this.#resumeTimeoutId = null;
      }
    }
  };
}

if (!customElements.get('hero-carousel-component')) {
  customElements.define('hero-carousel-component', HeroCarouselComponent);
}
